<template>
  <section
    class="card cl-statusbar"
    :class="{ 'is-dragging': dragging }"
    @dragover.prevent="dragging = true"
    @dragenter.prevent="dragging = true"
    @dragleave.prevent="dragging = false"
    @drop.prevent="onDrop"
  >
    <div class="cl-status-main">
      <span class="cl-status-dot" :class="statusClass" :title="statusTitle"></span>

      <AppDropdown
        class="cl-status-log"
        size="sm"
        label="Log"
        placeholder="No logs yet"
        empty-label="No logs found in this folder yet"
        :model-value="selectedFileId"
        :options="fileOptions"
        :disabled="!files.length"
        block
        @update:model-value="$emit('select', $event)"
      />

      <AppDropdown
        v-if="segmentOptions.length"
        class="cl-status-fight"
        size="sm"
        label="Fight"
        placeholder="Whole log"
        :model-value="selectedSegmentKey"
        :options="segmentOptions"
        show-meta
        block
        @update:model-value="$emit('selectSegment', $event)"
      />

      <span class="cl-status-facts">
        <span :title="directory.name || 'No folder connected'">
          <i class="fa-solid fa-folder"></i>
          {{ directory.connected ? directory.name : 'No folder' }}
        </span>
        <span><i class="fa-solid fa-list"></i> {{ files.length }}</span>
        <span :title="statusTitle"><i class="fa-solid fa-satellite-dish"></i> {{ shortMonitor }}</span>
        <span v-if="monitor.lastScanAt"><i class="fa-regular fa-clock"></i> {{ lastScanLabel }}</span>
        <span v-if="progressLabel" class="cl-status-progress"><i class="fa-solid fa-spinner fa-spin"></i> {{ progressLabel }}</span>
      </span>

      <span class="cl-status-actions">
        <button
          v-if="supported && !directory.connected"
          class="btn-sm"
          type="button"
          @click="$emit('connect')"
        >
          <i class="fa-solid fa-folder-open"></i> Choose folder
        </button>
        <button
          v-if="supported && directory.connected && directory.permission !== 'granted'"
          class="btn-sm"
          type="button"
          @click="$emit('requestAccess')"
        >
          <i class="fa-solid fa-unlock"></i> Restore access
        </button>
        <button
          v-if="directory.connected && directory.permission === 'granted'"
          class="btn-icon-outline"
          type="button"
          :disabled="monitor.scanning"
          aria-label="Rescan folder"
          title="Rescan folder"
          @click="$emit('refresh')"
        >
          <i class="fa-solid" :class="monitor.scanning ? 'fa-spinner fa-spin' : 'fa-rotate'"></i>
        </button>
        <button
          v-if="directory.connected && directory.permission === 'granted'"
          class="btn-icon-outline"
          type="button"
          :aria-label="monitorEnabled ? 'Pause monitoring' : 'Resume monitoring'"
          :title="monitorEnabled ? 'Pause monitoring' : 'Resume monitoring'"
          @click="$emit(monitorEnabled ? 'pause' : 'resume')"
        >
          <i class="fa-solid" :class="monitorEnabled ? 'fa-pause' : 'fa-play'"></i>
        </button>
        <button
          v-if="canExport"
          class="btn-icon-outline"
          type="button"
          aria-label="Export this analysis as JSON"
          title="Export this analysis as JSON"
          @click="$emit('export')"
        >
          <i class="fa-solid fa-file-export"></i>
        </button>
      </span>
    </div>
  </section>
</template>

<script>
import AppDropdown from '../AppDropdown.vue'
import { formatSize } from '../../services/combatLog/format'

const STATUS_LABELS = {
  new: 'Not analyzed',
  processing: 'Analyzing',
  updated: 'New data',
  error: 'Error',
  missing: 'Missing',
}

export default {
  name: 'CombatLogStatusBar',
  components: { AppDropdown },
  props: {
    supported: { type: Boolean, default: false },
    directory: { type: Object, required: true },
    monitor: { type: Object, required: true },
    monitorEnabled: { type: Boolean, default: true },
    monitorLabel: { type: String, default: '' },
    files: { type: Array, default: () => [] },
    selectedFileId: { type: String, default: '' },
    // The fight is picked here as well, so the log, the fight and the actions
    // that apply to them stay on one line instead of in two stacked bars.
    segmentOptions: { type: Array, default: () => [] },
    selectedSegmentKey: { type: String, default: '' },
    canExport: { type: Boolean, default: false },
    progress: { type: Object, default: null },
  },
  emits: ['connect', 'requestAccess', 'refresh', 'pause', 'resume', 'files', 'select', 'selectSegment', 'export'],
  data() {
    return { dragging: false }
  },
  computed: {
    statusClass() {
      if (!this.directory.connected && !this.files.length) return 'is-idle'
      if (this.directory.connected && this.directory.permission !== 'granted') return 'is-warn'
      if (this.monitor.running && this.monitorEnabled) return 'is-live'
      return 'is-idle'
    },
    statusTitle() {
      return `${this.permissionLabel} - ${this.monitorLabel}`
    },
    shortMonitor() {
      if (!this.monitor.running) return 'Off'
      if (!this.monitorEnabled) return 'Paused'
      if (this.monitor.observerActive) return 'Live'
      return `${Math.round((this.monitor.intervalMs || 0) / 1000)}s`
    },
    permissionLabel() {
      if (!this.directory.connected) return this.files.length ? 'Manual files' : 'Not connected'
      if (this.directory.permission === 'granted') return 'Granted'
      if (this.directory.permission === 'denied') return 'Denied'
      return 'Needs confirmation'
    },
    lastScanLabel() {
      const seconds = Math.round((Date.now() - this.monitor.lastScanAt) / 1000)
      if (seconds < 5) return 'now'
      if (seconds < 60) return `${seconds}s`
      return `${Math.round(seconds / 60)}m`
    },
    progressLabel() {
      if (!this.progress || !this.progress.size) return ''
      const percent = Math.min(100, Math.round((this.progress.offset / this.progress.size) * 100))
      return `${percent}%`
    },
    // The dropdown can carry a second line and a figure, so the log keeps its
    // zone and state instead of being flattened into one dash separated string.
    fileOptions() {
      return this.files.map((file) => {
        const zone = file.summary && file.summary.primaryZone ? file.summary.primaryZone.name : ''
        const state = STATUS_LABELS[file.status] || ''
        const hintParts = [zone, state].filter(Boolean)
        return {
          value: file.id,
          label: this.logLabel(file),
          hint: hintParts.join(' - '),
          meta: formatSize(file.size),
          icon: file.available === false ? 'fa-triangle-exclamation' : '',
        }
      })
    },
  },
  methods: {
    logLabel(file) {
      const name = file.name.replace(/^WoWCombatLog-?/, '').replace(/\.txt$/i, '')
      const date = file.lastModified
        ? new Date(file.lastModified).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        : ''
      return date ? `${date} - ${name}` : name
    },
    onDrop(event) {
      this.dragging = false
      const files = event.dataTransfer && event.dataTransfer.files
      if (files && files.length) this.$emit('files', files)
    },
  },
}
</script>
